import { useState, useMemo, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProductCard from "@/components/base/ProductCard";
import ScrollToTop from "@/components/base/ScrollToTop";
import StickyAddToCart from "./components/StickyAddToCart";
import { allProducts } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";

export default function ProductDetailPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { slug } = useParams<{ slug: string }>();

  const product = useMemo(
    () => allProducts.find((p) => p.slug === slug),
    [slug]
  );

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<"description" | "reviews">("description");
  const { addItem } = useCart();
  const { recentlyViewedProducts, addToRecentlyViewed } = useRecentlyViewed(product?.id);

  // Track this product view
  useEffect(() => {
    if (product) {
      addToRecentlyViewed(product.id);
    }
  }, [product?.id, addToRecentlyViewed]);

  // Initialize variant selections
  useState(() => {
    if (product?.variants) {
      const initial: Record<string, string> = {};
      product.variants.forEach((v) => {
        initial[v.name] = v.options[0];
      });
      setSelectedVariants(initial);
    }
  });

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return allProducts
      .filter((p) => p.categoryId === product.categoryId && p.id !== product.id)
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
        <Navbar />
        <main className="pt-20 pb-16 flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-900 mb-2">Product Not Found</h2>
            <p className="text-foreground-500 mb-6">The product you're looking for doesn't exist.</p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Browse Products
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const name = isAr ? product.nameAr : product.nameEn;
  const description = isAr ? product.descriptionAr : product.descriptionEn;
  const features = isAr ? product.featuresAr : product.featuresEn;
  const discount = product.compareAtPrice
    ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-4 border-b border-background-200">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-foreground-800 transition-colors whitespace-nowrap">
              {t("nav.home")}
            </Link>
            <span>/</span>
            <Link to="/products" className="hover:text-foreground-800 transition-colors whitespace-nowrap">
              {t("nav.products")}
            </Link>
            <span>/</span>
            <span className="text-foreground-900 font-medium truncate">{name}</span>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            {/* Product main section */}
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 ${isAr ? "lg:direction-rtl" : ""}`}>
              {/* Image Gallery */}
              <div className={`flex ${isAr ? "flex-row-reverse" : ""} gap-4`}>
                {/* Thumbnails */}
                <div className="flex flex-col gap-3 flex-shrink-0">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        selectedImage === idx
                          ? "border-primary-500"
                          : "border-background-200 hover:border-secondary-300"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>

                {/* Main image */}
                <div className="flex-1 aspect-square rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={product.images[selectedImage]}
                    alt={name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Product Info */}
              <div className="flex flex-col">
                {/* Badges */}
                <div className="flex items-center gap-2 mb-3">
                  {product.isSale && product.compareAtPrice && (
                    <span className="px-3 py-1 bg-primary-500 text-white text-xs font-semibold rounded-md whitespace-nowrap">
                      -{discount}% OFF
                    </span>
                  )}
                  {product.isNew && (
                    <span className="px-3 py-1 bg-foreground-950 text-white text-xs font-semibold rounded-md whitespace-nowrap">
                      NEW
                    </span>
                  )}
                </div>

                {/* Name */}
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  {name}
                </h1>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < Math.floor(product.rating)
                            ? "ri-star-fill text-accent-500"
                            : i < product.rating
                            ? "ri-star-half-line text-accent-500"
                            : "ri-star-line text-foreground-300"
                        }`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-sm text-foreground-600 font-medium">{product.rating}</span>
                  <span className="text-sm text-foreground-400">({product.reviewCount} reviews)</span>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-3 mb-6">
                  <span className="text-3xl font-bold text-foreground-950">
                    ${product.price.toFixed(2)}
                  </span>
                  {product.compareAtPrice && (
                    <>
                      <span className="text-lg text-foreground-400 line-through">
                        ${product.compareAtPrice.toFixed(2)}
                      </span>
                      <span className="text-sm font-medium text-primary-600">
                        Save ${(product.compareAtPrice - product.price).toFixed(2)}
                      </span>
                    </>
                  )}
                </div>

                {/* Stock status */}
                <div className="flex items-center gap-2 mb-6">
                  {product.inStock ? (
                    <>
                      <i className="ri-checkbox-circle-fill text-green-600"></i>
                      <span className="text-sm text-green-700 font-medium">
                        {t("product.in_stock")}
                      </span>
                      {product.stockCount < 20 && (
                        <span className="text-xs text-accent-700 bg-accent-50 px-2 py-0.5 rounded-full">
                          Only {product.stockCount} left
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <i className="ri-close-circle-fill text-red-500"></i>
                      <span className="text-sm text-red-600 font-medium">
                        {t("product.out_of_stock")}
                      </span>
                    </>
                  )}
                </div>

                {/* Variants */}
                {product.variants && product.variants.length > 0 && (
                  <div className="space-y-4 mb-6">
                    {product.variants.map((variant) => (
                      <div key={variant.name}>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-2.5">
                          {variant.name}:{" "}
                          <span className="font-normal text-foreground-600">
                            {selectedVariants[variant.name] || variant.options[0]}
                          </span>
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {variant.options.map((option) => {
                            const isSelected = (selectedVariants[variant.name] || variant.options[0]) === option;
                            // For color variants, we might want swatches, but keeping it simple with text for now
                            const isColor = variant.name.toLowerCase() === "color" || variant.name.toLowerCase() === "frame" || variant.name.toLowerCase() === "strap color" || variant.name.toLowerCase() === "lens";
                            return (
                              <button
                                key={option}
                                onClick={() =>
                                  setSelectedVariants((prev) => ({
                                    ...prev,
                                    [variant.name]: option,
                                  }))
                                }
                                className={`px-4 py-2 text-sm rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                                  isSelected
                                    ? "border-primary-500 bg-primary-50 text-primary-700 font-medium"
                                    : "border-background-200 text-foreground-600 hover:border-secondary-300"
                                }`}
                              >
                                {option}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quantity + Add to Cart */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-8">
                  <div className="flex items-center border border-background-200 rounded-lg">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer rounded-l-lg"
                      disabled={!product.inStock}
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-12 h-10 flex items-center justify-center text-sm font-medium text-foreground-900 border-x border-background-200">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity((q) => Math.min(product.stockCount, q + 1))}
                      className="w-10 h-10 flex items-center justify-center text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer rounded-r-lg"
                      disabled={!product.inStock}
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      if (!product.inStock) return;
                      addItem({
                        productId: product.id,
                        nameEn: product.nameEn,
                        nameAr: product.nameAr,
                        slug: product.slug,
                        price: product.price,
                        imageUrl: product.images[0],
                        quantity,
                        maxStock: product.stockCount,
                        selectedVariants,
                      });
                    }}
                    disabled={!product.inStock}
                    className="flex-1 flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-shopping-bag-line"></i>
                    {product.inStock ? t("product.add_to_cart") : t("product.out_of_stock")}
                  </button>

                  <button className="w-12 h-12 flex items-center justify-center border border-background-200 rounded-lg text-foreground-600 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer flex-shrink-0">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                </div>

                {/* Short info */}
                <div className="border-t border-background-200 pt-6 space-y-3">
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <i className="ri-truck-line text-foreground-400"></i>
                    <span>Free shipping on orders over $50</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <i className="ri-arrow-go-back-line text-foreground-400"></i>
                    <span>30-day easy returns</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <i className="ri-shield-check-line text-foreground-400"></i>
                    <span>1 year warranty included</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs: Description / Reviews */}
            <div className="mt-16">
              <div className="border-b border-background-200">
                <div className="flex gap-0">
                  {(["description", "reviews"] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-6 py-3.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                        activeTab === tab
                          ? "border-primary-500 text-primary-600"
                          : "border-transparent text-foreground-500 hover:text-foreground-700"
                      }`}
                    >
                      {tab === "description" ? t("product.description") : `${t("product.reviews")} (${product.reviewCount})`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="py-8">
                {activeTab === "description" ? (
                  <div className={`grid grid-cols-1 lg:grid-cols-3 gap-10 ${isAr ? "lg:direction-rtl" : ""}`}>
                    <div className="lg:col-span-2">
                      <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                        {description}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">Key Features</h4>
                      <ul className="space-y-2.5">
                        {features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 text-sm text-foreground-700">
                            <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div>
                    {product.reviews.length > 0 ? (
                      <div className="space-y-8">
                        {product.reviews.map((review) => (
                          <div key={review.id} className="pb-8 border-b border-background-200 last:border-0">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                                <img
                                  src={review.userAvatar}
                                  alt={review.userName}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-foreground-900">{review.userName}</p>
                                <p className="text-xs text-foreground-500">{review.date}</p>
                              </div>
                              <div className="flex items-center ml-auto">
                                {[...Array(5)].map((_, i) => (
                                  <i
                                    key={i}
                                    className={`text-xs ${
                                      i < review.rating
                                        ? "ri-star-fill text-accent-500"
                                        : "ri-star-line text-foreground-300"
                                    }`}
                                  ></i>
                                ))}
                              </div>
                            </div>
                            <p className="text-sm text-foreground-700 leading-relaxed">{review.text}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                          <i className="ri-chat-3-line text-2xl text-foreground-400"></i>
                        </div>
                        <p className="text-foreground-500">No reviews yet. Be the first to review this product!</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Related Products */}
            {relatedProducts.length > 0 && (
              <div className="mt-12 pt-12 border-t border-background-200">
                <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                  {t("product.related")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {relatedProducts.map((rp) => (
                    <ProductCard key={rp.id} {...rp} imageUrl={rp.images[0]} />
                  ))}
                </div>
              </div>
            )}

            {/* Recently Viewed */}
            {recentlyViewedProducts.length > 0 && (
              <div className="mt-12 pt-12 border-t border-background-200">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-sm text-accent-600"></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                    {t("product.recently_viewed")}
                  </h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {recentlyViewedProducts.map((rp) => (
                    <ProductCard key={rp.id} {...rp} imageUrl={rp.images[0]} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />

      {/* Sticky Add to Cart */}
      <StickyAddToCart
        productId={product.id}
        productName={name}
        productImage={product.images[0]}
        price={product.price}
        compareAtPrice={product.compareAtPrice}
        inStock={product.inStock}
        stockCount={product.stockCount}
        quantity={quantity}
        selectedVariants={selectedVariants}
        variants={product.variants}
        onQuantityChange={setQuantity}
      />
    </div>
  );
}